/**
 * App.jsx - Componente raiz da aplicação React
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AuthProvider, useAuth } from '@context/AuthContext'
import { NotificationProvider } from '@context/NotificationContext'
import { ThemeProvider } from '@context/ThemeContext'
import { PrazosProvider } from '@context/PrazosContext'
import AppHeader from '@components/shared/Header'
import LoginPage from '@components/login/LoginPage'
import BoardPage from '@components/board/BoardPage'
import HearingsPage from '@components/hearings/HearingsPage'
import DashboardPage from '@components/dashboard/DashboardPage'
import ProcessReportsPage from '@components/reports/ProcessReportsPage'
import ClickBusReport from '@components/reports/ClickBusReport'
import SystemSettingsPage from '@components/SystemSettingsPage'
import UsersPage from '@components/users/UsersPage'
import PublicationsMonitoringPage from '@components/publications/PublicationsMonitoringPage'
import DeadlineSimulatorPage from '@components/deadline-simulator/DeadlineSimulatorPage'
import DeadlineCounterPage from '@components/deadline-counter/DeadlineCounterPage'
import DeadlineEngineSettingsPage from '@components/deadline-simulator/DeadlineEngineSettingsPage'
import DeadlineCalendarManagementPage from '@components/deadline-simulator/DeadlineCalendarManagementPage'
import LegalTemplatesPage from '@components/legal-templates/LegalTemplatesPage'
import BaseJuridicaSettingsPage from '@components/legal-rag/BaseJuridicaSettingsPage'
import FormularioPublicoPage from '@components/public/FormularioPublicoPage'
import FormularioSubmissoesPage from '@components/public/FormularioSubmissoesPage'
import PortalApp from '@components/portal/PortalApp'
import PortalPasswordGate from '@components/portal/PortalPasswordGate'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { PageHeaderActionsContext } from '@context/PageHeaderContext'
import { APP_CONFIG } from '@config/constants'
import { isClientPortalSurface } from '@config/appSurface'

const PAGE_META = [
  { match: '/pauta-audiencias', index: '01', kicker: 'ControladorIA', title: 'Pauta de audiências', description: 'Consulte as audiências por advogado, estado, área e número do processo.' },
  { match: '/board', index: '01', kicker: 'ControladorIA', title: 'Kanban de Prazos', description: 'Gestão visual e operacional dos prazos judiciais e de agenda.' },
  { match: '/dashboard', index: '02', kicker: 'EficiencIA', title: 'Dashboard', description: 'Indicadores consolidados de produtividade, risco e execução.' },
  { match: '/relatorios/clickbus', index: '03', kicker: 'Adjutorem IA', title: 'Relatório ClickBus', description: 'Indicadores financeiros e evolução mensal da carteira ClickBus.' },
  { match: '/relatorios/processos', index: '03', kicker: 'Adjutorem IA', title: 'Processos por Cliente', description: 'Visão analítica da carteira de processos por cliente.' },
  { match: '/publicacoes-monitoramento', index: '04', kicker: 'PanaceIA', title: 'Publicações', description: 'Monitoramento das publicações vinculadas aos processos.' },
  { match: '/deadline-counter', index: '04', kicker: 'PanaceIA', title: 'Contador de Prazos', description: 'Contagem por UF com aplicação do calendário judicial de cada tribunal.' },
  { match: '/deadline-simulator', index: '04', kicker: 'PanaceIA', title: 'Simulador de Prazos', description: 'Teste independente do DeadlineEngine sem passar pelo monitoramento de publicações.' },
  { match: '/deadline-engine/configuracoes', index: '04', kicker: 'PanaceIA', title: 'Configurações do DeadlineEngine', description: 'Administração das regras e catálogos internos do motor de prazos.' },
  { match: '/calendario-juridico', index: '04', kicker: 'PanaceIA', title: 'Calendário jurídico', description: 'Gestão de dias não úteis, fontes oficiais e sincronizações judiciais.' },
  { match: '/modelos-pecas', index: '04', kicker: 'PanaceIA', title: 'Modelos de peças', description: 'Editor visual de documentos e modelos por área jurídica.' },
  { match: '/base-juridica/configuracoes', index: '04', kicker: 'PanaceIA', title: 'Configuração da base jurídica', description: 'Gerencie as fontes da IA, as peças por área e ação e o histórico dos documentos.' },
  { match: '/usuarios', index: '05', kicker: 'Administrativo', title: 'Usuários', description: 'Administração de acesso, perfis e permissões da equipe.' },
  { match: '/formulario-submissoes', index: '05', kicker: 'Administrativo', title: 'Fichas de Candidatos', description: 'Visualização e análise das fichas enviadas no formulário público.' },
  { match: '/configuracoes-sistema', index: 'SYS', kicker: 'Infraestrutura', title: 'Configurações de Sistema', description: 'Ajustes operacionais e políticas do ambiente.' }
]

const NO_ACCESS_PAGE_META = {
  index: '00',
  kicker: 'Acesso',
  title: 'Módulos indisponíveis',
  description: 'Não há módulos liberados para este perfil.',
}

function NoAccessibleModule() {
  return (
    <section className="app-access-empty" role="status">
      <span className="app-access-empty-index" aria-hidden="true">00</span>
      <div>
        <h2>Nenhum módulo disponível</h2>
        <p>Seu perfil está autenticado, mas ainda não possui permissão para acessar os módulos desta área.</p>
      </div>
    </section>
  )
}

function AppContent() {
  const {
    loading,
    isAuthenticated,
    isExternalClient,
    canAccessDashboard,
    canAccessReports,
    canAccessProcessReports,
    canAccessClickBusReports,
    canAccessBoard,
    canAccessUsers,
    canAccessSimulator,
    canManageSimulatorSettings,
    canManageLegalTemplates,
    canAccessForms,
    canAccessSystemSettings,
  } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const portalSurface = isClientPortalSurface()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [pageHeaderActions, setPageHeaderActions] = useState(null)
  const effectiveSidebarCollapsed = sidebarCollapsed && !sidebarOpen
  const accessiblePaths = [
    canAccessBoard ? '/board' : null,
    canAccessBoard ? '/pauta-audiencias' : null,
    canAccessDashboard ? '/dashboard' : null,
    canAccessReports && canAccessProcessReports ? '/relatorios/processos' : null,
    canAccessReports && canAccessClickBusReports ? '/relatorios/clickbus' : null,
    canAccessSystemSettings ? '/publicacoes-monitoramento' : null,
    canAccessSimulator ? '/deadline-counter' : null,
    canAccessSimulator ? '/deadline-simulator' : null,
    canManageSimulatorSettings ? '/calendario-juridico' : null,
    canManageLegalTemplates ? '/modelos-pecas' : null,
    canManageLegalTemplates || canManageSimulatorSettings ? '/base-juridica/configuracoes' : null,
    canManageSimulatorSettings ? '/deadline-engine/configuracoes' : null,
    canAccessUsers ? '/usuarios' : null,
    canAccessForms ? '/formulario-submissoes' : null,
    canAccessSystemSettings ? '/configuracoes-sistema' : null,
  ].filter(Boolean)
  const defaultAccessiblePath = accessiblePaths[0] || null
  const reportsDefaultPath = canAccessReports && canAccessProcessReports
    ? '/relatorios/processos'
    : canAccessReports && canAccessClickBusReports
      ? '/relatorios/clickbus'
      : null
  const fallbackRoute = defaultAccessiblePath
    ? <Navigate to={defaultAccessiblePath} replace />
    : <NoAccessibleModule />

  const currentPageMeta = useMemo(() => {
    if (!defaultAccessiblePath) {
      return NO_ACCESS_PAGE_META
    }

    const describedPath = location.pathname === '/' ? defaultAccessiblePath : location.pathname
    const fallbackMeta = PAGE_META.find((item) => defaultAccessiblePath.startsWith(item.match))

    return PAGE_META.find((item) => describedPath?.startsWith(item.match)) || fallbackMeta || NO_ACCESS_PAGE_META
  }, [defaultAccessiblePath, location.pathname])

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const handleViewportChange = () => {
      if (window.innerWidth > 1024) setSidebarOpen(false)
    }

    window.addEventListener('resize', handleViewportChange)
    return () => window.removeEventListener('resize', handleViewportChange)
  }, [])

  useEffect(() => {
    const isPublicForm = location.pathname === '/formulario' || window.location.hostname === 'seja.veiga.law'
    const internalAppActive = !portalSurface && isAuthenticated && !isPublicForm
    document.body.classList.toggle('internal-app-active', internalAppActive)

    return () => document.body.classList.remove('internal-app-active')
  }, [isAuthenticated, location.pathname, portalSurface])

  useEffect(() => {
    if (loading || portalSurface || !isAuthenticated || !isExternalClient) return
    if (window.location.hostname === 'localhost') return
    window.location.replace(APP_CONFIG.PORTAL_URL)
  }, [loading, portalSurface, isAuthenticated, isExternalClient])

  const handleSidebarToggle = useCallback(() => {
    if (window.innerWidth <= 1024) {
      setSidebarOpen((prev) => !prev)
      return
    }
    setSidebarCollapsed((prev) => !prev)
  }, [])

  if (location.pathname === '/formulario' || window.location.hostname === 'seja.veiga.law') {
    return <FormularioPublicoPage />
  }

  if (loading) {
    return (
      <div className="app-page-loading">
        <div className="loading-spinner loading-spinner-inverse">
          <div className="spinner" />
          <p>Carregando...</p>
        </div>
      </div>
    )
  }

  // O hostname define a superfície. O portal não monta sidebar, rotas ou componentes internos.
  if (portalSurface) {
    return <PortalPasswordGate><PortalApp /></PortalPasswordGate>
  }

  if (!isAuthenticated) return <LoginPage />

  if (isExternalClient && window.location.hostname !== 'localhost') {
    return (
      <div className="app-page-loading">
        <div className="loading-spinner loading-spinner-inverse">
          <div className="spinner" />
          <p>Redirecionando para o portal...</p>
        </div>
      </div>
    )
  }

  return (
    <PageHeaderActionsContext.Provider value={setPageHeaderActions}>
      <div className={`app-shell ${effectiveSidebarCollapsed ? 'is-sidebar-collapsed' : ''}`}>
        <AppHeader
          currentPath={location.pathname}
          onNavigate={navigate}
          sidebarOpen={sidebarOpen}
          sidebarCollapsed={effectiveSidebarCollapsed}
          onToggleSidebar={handleSidebarToggle}
          onCloseSidebar={() => setSidebarOpen(false)}
        />

        <div className="app-content-shell">
          <div className="app-content-topbar">
            <button
              type="button"
              className="sidebar-toggle-mobile"
              onClick={handleSidebarToggle}
              aria-label={sidebarOpen ? 'Fechar navegação' : 'Abrir navegação'}
              aria-expanded={sidebarOpen}
            >
              <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`} />
            </button>
            <div className="app-content-heading">
              <span className="app-content-index" aria-hidden="true">{currentPageMeta.index}</span>
              <div className="app-content-heading-copy">
                <span className="app-content-kicker">{currentPageMeta.kicker}</span>
                <h1>{currentPageMeta.title}</h1>
                <p>{currentPageMeta.description}</p>
              </div>
            </div>
            {pageHeaderActions ? <div className="app-content-actions">{pageHeaderActions}</div> : null}
          </div>

          <main className="app-main">
            <Routes>
              <Route path="/" element={fallbackRoute} />
              <Route path="/board" element={canAccessBoard ? <PrazosProvider><BoardPage /></PrazosProvider> : fallbackRoute} />
              <Route path="/pauta-audiencias" element={canAccessBoard ? <HearingsPage /> : fallbackRoute} />
              <Route path="/dashboard" element={canAccessDashboard ? <DashboardPage /> : fallbackRoute} />
              <Route path="/relatorios" element={reportsDefaultPath ? <Navigate to={reportsDefaultPath} replace /> : fallbackRoute} />
              <Route path="/relatorios/processos" element={canAccessReports && canAccessProcessReports ? <ProcessReportsPage /> : fallbackRoute} />
              <Route path="/relatorios/clickbus" element={canAccessReports && canAccessClickBusReports ? <ClickBusReport /> : fallbackRoute} />
              <Route path="/usuarios" element={canAccessUsers ? <UsersPage /> : fallbackRoute} />
              <Route path="/publicacoes-monitoramento" element={canAccessSystemSettings ? <PublicationsMonitoringPage /> : fallbackRoute} />
              <Route path="/formulario-submissoes" element={canAccessForms ? <FormularioSubmissoesPage /> : fallbackRoute} />
              <Route path="/configuracoes-sistema" element={canAccessSystemSettings ? <SystemSettingsPage /> : fallbackRoute} />
              <Route path="/deadline-counter" element={canAccessSimulator ? <DeadlineCounterPage /> : fallbackRoute} />
              <Route path="/deadline-simulator" element={canAccessSimulator ? <DeadlineSimulatorPage /> : fallbackRoute} />
              <Route path="/calendario-juridico" element={canManageSimulatorSettings ? <DeadlineCalendarManagementPage /> : fallbackRoute} />
              <Route path="/modelos-pecas" element={canManageLegalTemplates ? <LegalTemplatesPage /> : fallbackRoute} />
              <Route path="/base-juridica/configuracoes" element={canManageLegalTemplates || canManageSimulatorSettings ? <BaseJuridicaSettingsPage /> : fallbackRoute} />
              <Route path="/acervo-pecas" element={canManageLegalTemplates ? <Navigate to="/base-juridica/configuracoes?secao=pecas" replace /> : fallbackRoute} />
              <Route path="/deadline-engine/configuracoes" element={canManageSimulatorSettings ? <DeadlineEngineSettingsPage /> : fallbackRoute} />
              <Route path="*" element={fallbackRoute} />
            </Routes>
          </main>
        </div>
      </div>
    </PageHeaderActionsContext.Provider>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </ThemeProvider>
    </AuthProvider>
  )
}
